import React, { useEffect, useState } from 'react';
import { CheckCircle2, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const fmt = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plus2y = (d) => (d ? `${Number(d.slice(0, 4)) + 2}${d.slice(4, 10) === '-02-29' ? '-02-28' : d.slice(4, 10)}` : '');
const tone = (v) => (v > 0 ? 'text-red-600' : v < 0 ? 'text-emerald-600' : 'text-gray-600');
const err = (e) => toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });

export const ActualsDialog = ({ row, canEdit, onClose, onSaved }) => {
  const [actual, setActual] = useState({});
  const [extras, setExtras] = useState([]);
  const [srcs, setSrcs] = useState({});
  const [paid, setPaid] = useState('');
  const [note, setNote] = useState('');
  const [newDate, setNewDate] = useState('');
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!row) return;
    const a = row.actuals || {};
    const map = {};
    const src = {};
    (a.lines || []).forEach((l) => { map[l.item_id || l.description] = l.actual; });
    const matched = (l) => row.items.find((i) => i.id === l.item_id || i.description.toLowerCase() === (l.description || '').toLowerCase());
    (a.lines || []).forEach((l) => { const m = matched(l); if (m && l.source) src[m.id] = l.source; });
    setSrcs(src);
    setExtras((a.lines || []).filter((l) => !matched(l)));
    setActual(Object.fromEntries(row.items.map((i) => [i.id, map[i.id] ?? map[i.description] ?? ''])));
    setPaid(a.paid_date || ''); setNote(a.note || ''); setNewDate(plus2y(row.renewal_date));
    api.get(`/api/visa-costs/records/${row.employee_id}/history`).then((r) => setHist(r.data)).catch(() => setHist([]));
  }, [row]);

  if (!row) return null;
  const budget = row.items.reduce((s, i) => s + i.amount, 0);
  const extraTotal = extras.reduce((s, l) => s + (Number(l.actual) || 0), 0);
  const actualTotal = row.items.reduce((s, i) => s + (Number(actual[i.id]) || 0), 0) + extraTotal;
  const lines = () => [
    ...row.items.map((i) => ({ item_id: i.id, description: i.description, actual: Number(actual[i.id]) || 0, source: srcs[i.id] || '' })),
    ...extras.map((l) => ({ item_id: '', description: l.description, actual: Number(l.actual) || 0, source: l.source || '' })),
  ];

  const save = async () => {
    setBusy('save');
    try { await api.put(`/api/visa-costs/records/${row.employee_id}/actuals`, { lines: lines(), paid_date: paid || null, note }); toast({ title: 'Actuals saved' }); onSaved(false); }
    catch (e) { err(e); } finally { setBusy(''); }
  };
  const complete = async () => {
    if (!window.confirm(`Complete this renewal and move the renewal date to ${newDate}? Budget vs actual will be kept in history.`)) return;
    setBusy('complete');
    try {
      await api.put(`/api/visa-costs/records/${row.employee_id}/actuals`, { lines: lines(), paid_date: paid || null, note });
      await api.post(`/api/visa-costs/records/${row.employee_id}/complete-renewal`, { new_renewal_date: newDate });
      toast({ title: 'Renewal completed', description: `Next renewal: ${newDate}` });
      onSaved(true);
    } catch (e) { err(e); } finally { setBusy(''); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vc-actuals-dialog">
        <DialogHeader><DialogTitle>Actual vs budget — {row.name} <span className="text-sm text-gray-500 font-normal">renewal {row.renewal_date || '—'}</span></DialogTitle></DialogHeader>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-gray-500"><tr><th className="text-left py-1">Cost line</th><th className="text-right">Budget</th><th className="text-right w-36">Actual paid</th><th className="text-right">Variance</th></tr></thead>
          <tbody className="divide-y">
            {row.items.map((i) => {
              const v = (Number(actual[i.id]) || 0) - i.amount;
              return (
                <tr key={i.id}>
                  <td className="py-1.5">{i.description}{i.cost_centre_code && <span className="text-xs text-gray-400"> · {i.cost_centre_code}</span>}</td>
                  <td className="text-right">{fmt(i.amount)}</td>
                  <td className="text-right"><Input disabled={!canEdit} type="number" min={0} className="h-8 text-right" value={actual[i.id]} onChange={(e) => setActual({ ...actual, [i.id]: e.target.value })} data-testid={`vc-actual-${i.id}`} /></td>
                  <td className={`text-right ${tone(v)}`}>{actual[i.id] === '' ? '—' : fmt(v)}</td>
                </tr>
              );
            })}
            {extras.map((l, n) => (
              <tr key={`x-${n}`} className="bg-emerald-50/40" data-testid={`vc-actual-extra-${n}`}>
                <td className="py-1.5">{l.description} <span className="text-[10px] text-emerald-700">{l.source === 'tracker' ? '· from Renewal Tracker' : '· unbudgeted'}</span></td>
                <td className="text-right text-gray-400">—</td>
                <td className="text-right pr-3">{fmt(l.actual)}</td>
                <td className={`text-right ${tone(Number(l.actual))}`}>{fmt(l.actual)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="font-semibold"><tr><td className="py-1.5">Total</td><td className="text-right">{fmt(budget)}</td><td className="text-right" data-testid="vc-actual-total">{fmt(actualTotal)}</td><td className={`text-right ${tone(actualTotal - budget)}`} data-testid="vc-actual-variance">{fmt(actualTotal - budget)}</td></tr></tfoot>
        </table>
        {!row.items.length && !extras.length && <p className="text-sm text-gray-500">Add cost lines first (Edit) to compare against a budget.</p>}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Paid date</Label><Input disabled={!canEdit} type="date" className="h-9" value={paid} onChange={(e) => setPaid(e.target.value)} data-testid="vc-actual-paid-date" /></div>
          <div><Label className="text-xs">Note</Label><Input disabled={!canEdit} className="h-9" value={note} onChange={(e) => setNote(e.target.value)} data-testid="vc-actual-note" /></div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={save} disabled={!!busy} data-testid="vc-actual-save">{busy === 'save' ? 'Saving…' : 'Save actuals'}</Button>
            <div className="ml-auto flex items-end gap-2">
              <div><Label className="text-xs">Next renewal date</Label><Input type="date" className="h-9 w-40" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="vc-complete-new-date" /></div>
              <Button onClick={complete} disabled={!!busy || !newDate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="vc-complete-renewal"><CheckCircle2 className="h-4 w-4 mr-1" />{busy === 'complete' ? 'Completing…' : 'Complete renewal'}</Button>
            </div>
          </div>
        )}
        <div className="border-t pt-3">
          <p className="text-sm font-medium flex items-center gap-1 mb-1"><History className="h-4 w-4" />Renewal history ({hist.length})</p>
          {hist.map((h) => (
            <div key={h.id} className="text-xs flex flex-wrap gap-x-3 py-1 border-b last:border-0" data-testid={`vc-history-${h.id}`}>
              <span><b>{h.renewal_date || '—'}</b> → {h.new_renewal_date}</span>
              <span>Budget {fmt(h.budget_total)}</span><span>Actual {fmt(h.actual_total)}</span>
              <span className={tone(h.variance)}>Variance {fmt(h.variance)}</span>
              <span className="text-gray-400">paid {h.paid_date || '—'} · by {h.completed_by}</span>
            </div>
          ))}
          {!hist.length && <p className="text-xs text-gray-400">No completed renewals yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
